import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API = '/api';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    vendors: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
    truck: ['M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M5 17H3V7c0-1.1.9-2 2-2h11v12h-2', 'M19 17h2v-6l-3-4h-2v7h4'],
    file: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8', 'M10 9H8'],
    contract: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', 'M8 11h8', 'M8 15h8'],
    dollar: 'M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
    activity: 'M22 12h-4l-3 9L9 3l-3 9H2',
    chevronRight: 'M9 18l6-6-6-6'
};

export default function VendorDashboard({ user, operatorDetails }) {
    const [stats, setStats] = useState({
        fleetCount: 0,
        docCount: 0,
        contractCount: 0,
        pricingCount: 0,
        pricingCount: 0,
        pricingCount: 0,
        topVendors: [],
        recentOperations: []
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const opId = operatorDetails?._id || user?.operatorId;
            const [fleet, docs, contracts, pricing] = await Promise.all([
                axios.get(`${API}/vendor-fleet/${opId}`),
                axios.get(`${API}/vendor-documents/${opId}`),
                axios.get(`${API}/vendor-contracts/${opId}`),
                axios.get(`${API}/vendor-pricing/${opId}`)
            ]);

            // Calculate top vendors by fleet size
            const vendorFleetCounts = {};
            fleet.data.forEach(v => {
                const name = v.vendorId?.vendorName || 'Unknown Vendor';
                vendorFleetCounts[name] = (vendorFleetCounts[name] || 0) + 1;
            });
            
            const topVendors = Object.entries(vendorFleetCounts)
                .map(([name, count]) => ({ name, count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 5);

            const timeAgo = (dateStr) => {
                const diff = (new Date() - new Date(dateStr)) / 1000;
                if (diff < 60) return 'Just now';
                if (diff < 3600) return `${Math.floor(diff/60)} mins ago`;
                if (diff < 86400) return `${Math.floor(diff/3600)} hours ago`;
                return `${Math.floor(diff/86400)} days ago`;
            };

            const activities = [];
            fleet.data.forEach(v => {
                if (v.createdAt || v.updatedAt) {
                    activities.push({
                        date: new Date(v.createdAt || v.updatedAt),
                        text: `New fleet vehicle added by <strong>${v.vendorId?.vendorName || 'Unknown'}</strong>`,
                    });
                }
            });
            docs.data.forEach(d => {
                if (d.createdAt || d.updatedAt) {
                    activities.push({
                        date: new Date(d.createdAt || d.updatedAt),
                        text: `Documentation uploaded for <strong>${d.vendorId?.vendorName || 'Unknown'}</strong>`,
                    });
                }
            });
            contracts.data.forEach(c => {
                if (c.createdAt || c.updatedAt) {
                    activities.push({
                        date: new Date(c.createdAt || c.updatedAt),
                        text: `Contract added for <strong>${c.vendorId?.vendorName || 'Unknown'}</strong>`,
                    });
                }
            });
            pricing.data.forEach(p => {
                if (p.createdAt || p.updatedAt) {
                    activities.push({
                        date: new Date(p.createdAt || p.updatedAt),
                        text: `Pricing list updated for <strong>${p.vendorId?.vendorName || 'Unknown'}</strong>`,
                    });
                }
            });

            activities.sort((a, b) => b.date - a.date);
            const recentOperations = activities.slice(0, 5).map(a => ({
                text: a.text,
                time: timeAgo(a.date)
            }));

            setStats({
                fleetCount: fleet.data.length,
                docCount: docs.data.length,
                contractCount: contracts.data.length,
                pricingCount: pricing.data.length,
                topVendors,
                recentOperations
            });
        } catch (err) {
            console.error("Fetch Stats error:", err);
        } finally {
            setLoading(false);
        }
    };

    const cards = [
        { label: 'Total Fleet', val: stats.fleetCount, icon: icons.truck, color: '#00B2A9', bg: '#eef2ff' },
        { label: 'Documents', val: stats.docCount, icon: icons.file, color: '#f59e0b', bg: '#fffbeb' },
        { label: 'Contracts', val: stats.contractCount, icon: icons.contract, color: '#10b981', bg: '#ecfdf5' },
        { label: 'Pricing Lists', val: stats.pricingCount, icon: icons.dollar, color: '#ef4444', bg: '#fef2f2' },
    ];

    return (
        <div className="vd-container">
            <style>{`
                .vd-container { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .vd-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; }
                .title-wrap h1 { font-size: 24px; font-weight: 800; color: #1e293b; margin: 0 0 4px 0; }
                .title-wrap p { color: #64748b; font-size: 14px; margin: 0; }

                .vd-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
                    gap: 24px;
                    margin-bottom: 48px;
                }
                .vd-card {
                    background: white; border-radius: 20px; padding: 24px;
                    border: 1px solid #e2e8f0; border-bottom: 4px solid var(--accent);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    display: flex; flex-direction: column; gap: 16px;
                }
                .vd-card:hover { transform: translateY(-4px); box-shadow: 0 12px 24px -10px rgba(0,0,0,0.1); }
                
                .vd-card-header { display: flex; align-items: center; justify-content: space-between; }
                .vd-card-icon {
                    width: 48px; height: 48px; border-radius: 14px;
                    display: flex; align-items: center; justify-content: center;
                }
                .vd-card-val { font-size: 28px; font-weight: 800; color: #1e293b; margin-top: 4px; }
                .vd-card-label { font-size: 13px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }

                .vd-sections { display: grid; grid-template-columns: 2fr 1.2fr; gap: 32px; }
                .vd-panel {
                    background: white; border-radius: 24px; padding: 32px;
                    border: 1px solid #e2e8f0;
                }
                .vd-panel-title { font-size: 18px; font-weight: 700; color: #1e293b; margin-bottom: 24px; display: flex; align-items: center; gap: 10px; }
                
                .vd-cat-list { display: flex; flex-direction: column; gap: 16px; }
                .vd-cat-item {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 16px 20px; background: #f8fafc; border-radius: 16px;
                    transition: all 0.2s;
                }
                .vd-cat-item:hover { background: #f1f5f9; }
                .vd-cat-info { display: flex; align-items: center; gap: 12px; }
                .vd-cat-dot { width: 8px; height: 8px; border-radius: 50%; background: #00B2A9; }
                .vd-cat-name { font-weight: 600; color: #334155; }
                .vd-cat-count { font-weight: 700; color: #00B2A9; background: #eef2ff; padding: 4px 12px; border-radius: 20px; font-size: 13px; }

                .vd-activity-item {
                    display: flex; gap: 16px; margin-bottom: 20px; position: relative;
                }
                .vd-activity-line {
                    position: absolute; left: 17px; top: 34px; bottom: -10px;
                    width: 2px; background: #f1f5f9;
                }
                .vd-activity-item:last-child .vd-activity-line { display: none; }
                .vd-activity-dot {
                    width: 36px; height: 36px; border-radius: 50%; background: #f8fafc;
                    display: flex; align-items: center; justify-content: center;
                    border: 2px solid white; box-shadow: 0 0 0 2px #f1f5f9; z-index: 1;
                }
                .vd-activity-content { flex: 1; padding-top: 6px; }
                .vd-activity-text { font-size: 14px; color: #475569; }
                .vd-activity-text strong { color: #1e293b; }
                .vd-activity-time { font-size: 12px; color: #94a3b8; margin-top: 4px; }
            `}</style>

            <header className="vd-header">
                <div className="title-wrap">
                    <h1>Vendor Dashboard</h1>
                    <p>Integrate fleets, documents, and pricing across your partner network.</p>
                </div>
            </header>

            <div className="vd-grid">
                {cards.map((c, i) => (
                    <div key={i} className="vd-card" style={{ '--accent': c.color }}>
                        <div className="vd-card-header">
                            <span className="vd-card-label">{c.label}</span>
                            <div className="vd-card-icon" style={{ background: c.bg, color: c.color }}>
                                <Icon d={c.icon} size={24} />
                            </div>
                        </div>
                        <div className="vd-card-val">{loading ? '...' : c.val}</div>
                    </div>
                ))}
            </div>

            <div className="vd-sections">
                <div className="vd-panel">
                    <h2 className="vd-panel-title">
                        <Icon d={icons.vendors} size={20} color="#00B2A9" />
                        Top Fleet Partners
                    </h2>
                    <div className="vd-cat-list">
                        {stats.topVendors.length > 0 ? stats.topVendors.map((v, i) => (
                            <div key={i} className="vd-cat-item">
                                <div className="vd-cat-info">
                                    <div className="vd-cat-dot" />
                                    <span className="vd-cat-name">{v.name}</span>
                                </div>
                                <span className="vd-cat-count">{v.count} Vehicles</span>
                            </div>
                        )) : (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
                                No fleet data available
                            </div>
                        )}
                    </div>
                </div>

                <div className="vd-panel">
                    <h2 className="vd-panel-title">
                        <Icon d={icons.activity} size={20} color="#f59e0b" />
                        Recent Operations
                    </h2>
                    <div className="vd-activity-list">
                        {stats.recentOperations.length > 0 ? stats.recentOperations.map((a, i) => (
                            <div key={i} className="vd-activity-item">
                                <div className="vd-activity-line" />
                                <div className="vd-activity-dot">
                                    <Icon d={icons.chevronRight} size={14} color="#94a3b8" />
                                </div>
                                <div className="vd-activity-content">
                                    <p className="vd-activity-text" dangerouslySetInnerHTML={{ __html: a.text }} />
                                    <p className="vd-activity-time">{a.time}</p>
                                </div>
                            </div>
                        )) : (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
                                No recent operations
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
